import React, { FC } from 'react'
import { MainNavBannerContainer, NewNavBannerContainer } from './style'

//* utils
import { ModuleNames } from '../../../constants/componentNames.constants'
import ImgUtil from '../../../utils/img.utils'
import { correctSlugPrefix, isExternalURL } from '../../../utils/link.utils'
import formatInlineCode from '../../../utils/shortTextFormatting'

//* components
import Button from '../button'
import Typography from '../typography'
import WrapperImage from '../wrapperImage'

type Props = {
  currentNav: any
  navType: string
}

export const RenderBanner: FC<Props> = ({ currentNav, navType }) => {
  const {
    theme,
    eyebrow,
    title,
    subtitle,
    description,
    textAlignment,
    useCase,
    icon,
    image,
    cta,
    displayCta,
  } = currentNav

  const imageAlt = icon?.altText || image?.altText || ''
  const imageSrc = icon?.image?.file?.url || image?.image?.file?.url || ''
  const backgroundImgSrc = image?.image?.file?.url || ''
  let imageWidth = useCase === 'Icon Text' ? '64' : '426'

  const internalLinkUrl = cta?.internalLink?.slug ? correctSlugPrefix(cta.internalLink.slug) : ''
  const ctaText = cta?.text || cta?.ctaText || ''
  const ctaUrl = cta?.externalUrl || internalLinkUrl || ''
  const isExternal = cta?.__typename === ModuleNames.TOPIC_CTA

  const bannerImage = (
    <div className="MainNavBanner__Image-Container">
      <WrapperImage
        loading="lazy"
        alt={imageAlt}
        src={ImgUtil.getResizedImageUrl(imageSrc, `width=${imageWidth}`)}
        threshold={650}
      />
    </div>
  )

  const bannerEyebrow = (
    <Typography className="MainNavBanner__Eyebrow" type="microAlt">
      {formatInlineCode(eyebrow)}
    </Typography>
  )

  const bannerTitle = (
    <Typography className="MainNavBanner__Title" type="label">
      {formatInlineCode(title)}
    </Typography>
  )

  const bannerSubtitle = (
    <Typography className="MainNavBanner__Subtitle" type="caption">
      {formatInlineCode(subtitle)}
    </Typography>
  )

  const bannerDescription = (
    <Typography className="MainNavBanner__Description" type="caption">
      {formatInlineCode(description)}
    </Typography>
  )

  const bannerCta = (
    <Button
      className="MainNavBanner__CTA"
      icon={true}
      href={ctaUrl}
      text={ctaText}
      type="text-link"
      size="small"
      theme={theme === 'light' || theme === 'grey' ? 'light' : 'dark'}
    />
  )

  const navPromoCard = (
    <a
      href={ctaUrl}
      target={isExternalURL(ctaUrl) ? '_blank' : '_self'}
      className="MainNavBanner__Promo-Card-Link"></a>
  )

  const originalMainNavBanner = (
    <MainNavBannerContainer
      data-cy={`MainNavBanner-${useCase.split(' ').join('_')}`}
      $useCase={useCase}
      $backgroundTheme={theme}
      $navType={navType}>
      <a href={ctaUrl} target={isExternal ? '_blank' : '_self'}>
        <div className="MainNavBanner__Inner-Container">
          {ctaUrl && navPromoCard}
          {imageSrc && bannerImage}

          <div className="MainNavBanner__Content">
            {title && bannerTitle}
            {subtitle && bannerSubtitle}
            {ctaText && ctaUrl && bannerCta}
          </div>
        </div>
      </a>
    </MainNavBannerContainer>
  )

  const newMainNavBanner = (
    <NewNavBannerContainer
      $useCase={useCase}
      $backgroundImgSrc={backgroundImgSrc}
      $displayCta={displayCta}
      $textAlignment={textAlignment}>
      <div className="MainNavBanner__Inner-Container">
        {ctaUrl && navPromoCard}
        {imageSrc && useCase === 'Text Below' && bannerImage}

        <div className="MainNavBanner__Content">
          {eyebrow && bannerEyebrow}
          {title && bannerTitle}
          {subtitle && bannerSubtitle}
          {description && bannerDescription}
          {displayCta && ctaText && ctaUrl && bannerCta}
        </div>
      </div>
    </NewNavBannerContainer>
  )

  return navType === 'New Nav' ? newMainNavBanner : originalMainNavBanner
}
